<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="パケットフィルタリングルール設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- プロトコル -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="プロトコル"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      inline
                      v-model="form.protocol"
                      :options="protocolOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in protocolOptions"
                        :key="option.value"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!-- アクション -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="アクション"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      inline
                      v-model="form.action"
                      :options="actionOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in actionOptions"
                        :key="option.value"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row>
                  <!-- 送信元アドレス -->
                  <v-col cols="6">
                    <app-badge
                      item="送信元アドレス"
                      :isRequired="srcAddress !== null"
                      :isInfo="srcAddress !== null"
                      :msg="
                        $filter.message(
                          'info_cidr_ip_address_of_network_address',
                          {
                            ipaddress1: '192.168.0.0/24',
                            genre: '送信元アドレス',
                            subnet1: '1',
                            subnet2: '30',
                            ipaddress2: '192.168.0.1/32',
                          }
                        )
                      "
                    />
                    <v-radio-group
                      inline
                      v-model="srcAddress"
                      :options="addrOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in addrOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                    <validation-provider
                      v-if="srcAddress !== null"
                      name="srcAddress"
                      v-model="form.srcAddress"
                      label="送信元アドレス"
                      :rules="{
                        required: true,
                        is_ip_subnet: { min: 1, max: 32, exclude: [31] },
                        is_network_address: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                        :placeholder="
                          $filter.message(
                            'placeholder_ip_address_of_private_network_address'
                          )
                        "
                      />
                    </validation-provider>
                    <app-form-input v-else disabled />
                  </v-col>
                  <!-- 宛先アドレス -->
                  <v-col cols="6">
                    <app-badge
                      item="宛先アドレス"
                      :isRequired="dstAddress !== null"
                      :isInfo="dstAddress !== null"
                      :msg="
                        $filter.message(
                          'info_cidr_ip_address_of_network_address',
                          {
                            ipaddress1: '192.168.0.0/24',
                            genre: '宛先アドレス',
                            subnet1: '1',
                            subnet2: '30',
                            ipaddress2: '192.168.0.1/32',
                          }
                        )
                      "
                    />
                    <v-radio-group
                      inline
                      v-model="dstAddress"
                      :options="addrOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in addrOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                    <validation-provider
                      v-if="dstAddress !== null"
                      name="dstAddress"
                      v-model="form.dstAddress"
                      label="宛先アドレス"
                      :rules="{
                        required: true,
                        is_ip_subnet: { min: 1, max: 32, exclude: [31] },
                        is_network_address: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                        :placeholder="
                          $filter.message(
                            'placeholder_ip_address_of_private_network_address'
                          )
                        "
                      />
                    </validation-provider>
                    <app-form-input v-else disabled />
                  </v-col>
                </v-row>
                <v-row>
                  <!-- 送信元ポート -->
                  <v-col cols="6">
                    <app-badge
                      item="送信元ポート"
                      :isRequired="srcPort !== null"
                      :isInfo="srcPort !== null"
                      :msg="$filter.message('info_src_dst_port')"
                    />
                    <v-radio-group
                      inline
                      v-model="srcPort"
                      :options="portOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in portOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                        :disabled="form.protocol === 'ANY'"
                      />
                    </v-radio-group>
                    <validation-provider
                      v-if="srcPort !== null"
                      name="srcPort"
                      v-model="form.srcPort"
                      label="送信元ポート"
                      :rules="{
                        required: true,
                        is_port: { minVal: 0 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                    <app-form-input v-else disabled />
                  </v-col>
                  <!-- 宛先ポート -->
                  <v-col cols="6">
                    <app-badge
                      item="宛先ポート"
                      :isRequired="dstPort !== null"
                      :isInfo="dstPort !== null"
                      :msg="$filter.message('info_src_dst_port')"
                    />
                    <v-radio-group
                      inline
                      v-model="dstPort"
                      :options="portOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in portOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                        :disabled="form.protocol === 'ANY'"
                      />
                    </v-radio-group>
                    <validation-provider
                      v-if="dstPort !== null"
                      name="dstPort"
                      v-model="form.dstPort"
                      label="宛先ポート"
                      :rules="{
                        required: true,
                        is_port: { minVal: 0 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                    <app-form-input v-else disabled />
                  </v-col>
                </v-row>
                <!-- 有効・無効 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="有効・無効"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      inline
                      v-model="form.filteringStatus"
                      :options="statusOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in statusOptions"
                        :key="option.value"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :isRequired="false"
                    />
                    <validation-provider
                      name="description"
                      v-model="form.description"
                      label="備考"
                      :rules="{
                        max: { length: 20 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  isEdit ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { PacketFilterEntity, Protocol } from "@/apis/PacketFilteringApi";
import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  name: "PacketFilteringRuleEdit",
  props: {
    /** ルール設定（変更時必須） */
    packetData: {
      type: Object as PropType<PacketFilterEntity>,
      required: false,
    },
  },
  data() {
    return {
      // APIに渡さない画面表示用
      srcAddress: this.packetData?.srcAddress ? "address" : null,
      dstAddress: this.packetData?.dstAddress ? "address" : null,
      srcPort: this.packetData?.srcPort ? "port" : null,
      dstPort: this.packetData?.dstPort ? "port" : null,

      form: cloneDeep(this.packetData) ?? {
        packetFilterRuleSeq: null,
        isDefaultRule: false,
        protocol: "TCP",
        action: "PERMIT",
        srcAddress: null,
        dstAddress: null,
        srcPort: null,
        dstPort: null,
        filteringStatus: "DISABLED",
        description: null,
      },
      addrOptions: [
        { value: null, text: "any" },
        { value: "address", text: "アドレス指定" },
      ],
      portOptions: [
        { value: null, text: "any" },
        { value: "port", text: "ポート指定" },
      ],
      protocolOptions: [
        { value: "TCP", text: "TCP" },
        { value: "UDP", text: "UDP" },
        { value: "ANY", text: "any" },
      ],
      actionOptions: [
        { value: "PERMIT", text: "Permit" },
        { value: "DENY", text: "Deny" },
      ],
      statusOptions: filterStatusItems,
    };
  },
  computed: {
    /** true: 編集, false: 新規 */
    isEdit(): boolean {
      return !!this.packetData;
    },
  },
  watch: {
    /** プロトコルでanyが選択された場合にポートをanyに変更 */
    "form.protocol"(newValue: Protocol) {
      if (newValue === "ANY") {
        this.srcPort = null;
        this.dstPort = null;
      }
    },
  },
  methods: {
    submit() {
      (
        this.$refs.modal as typeof VueFinalModal & {
          ok: (r: unknown) => void;
        }
      ).ok({
        ...this.form,
        srcAddress: this.srcAddress === "address" ? this.form.srcAddress : null,
        dstAddress: this.dstAddress === "address" ? this.form.dstAddress : null,
        srcPort: this.srcPort === "port" ? this.form.srcPort : null,
        dstPort: this.dstPort === "port" ? this.form.dstPort : null,
      });
    },
  },
});

/** フィルタリング有効/無効 */
const filterStatusItems = [
  { value: "ENABLED", text: "有効" },
  { value: "DISABLED", text: "無効" },
];
</script>
